import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { CheckableLineItem, LineDamage } from '../../../service/receiving-mobile/log-entry-draft.store';
import { LineItemCheckoffComponent } from '../line-item-checkoff/line-item-checkoff.component';

/**
 * items-entry.component.ts
 *
 * "What arrived" as a whole: the PO line-item check-off, plus the way to record
 * something that was NOT on the PO. Rendered by both the mobile submit's Items step
 * and the receiving-log edit screen.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS LAYER EXISTS
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * {@link LineItemCheckoffComponent} is deliberately only about PO LINES, and its
 * specs are worth more for staying that way. But an off-PO addition is not a PO
 * line — it has no ordered quantity, no line sequence, and nothing to fall short
 * of — so bolting its form onto that component would have made "the check-off"
 * mean two things.
 *
 * This wrapper is the honest home for the pair: it owns the add form, forwards
 * everything else straight through, and gives both hosts one tag to render instead
 * of two they have to keep in the same order.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IT STILL DOES NOT OWN
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * - **The items STRING.** Both hosts show the composed note, but they compose it
 *   differently and cannot share the computation: the submit builds it from the
 *   draft (`buildItemsString` with its manual text and PO-complete flag), while the
 *   edit screen rebuilds only the derivable clauses and preserves the human ones
 *   (`recomposeItemsNote`). Only the presentation could be shared, and the two
 *   previews carry different extras — mobile's has a missing-items shortcut this
 *   screen has no equivalent of.
 * - **Damage**, **missing items**, **PO completion**, and where the rows came from.
 *   See the check-off's class doc; none of that changed by adding a layer.
 * - **Editing or removing an existing off-PO row.** Mobile does that through its own
 *   Edit/Remove buttons in {@link linePanel}, because the description was typed on
 *   that screen and is edited with the quantity in one form. The edit screen treats
 *   the description as stored history and offers only the quantity.
 */
@Component({
  selector: 'app-items-entry',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, LineItemCheckoffComponent],
  templateUrl: './items-entry.component.html',
  styleUrls: ['./items-entry.component.css'],
})
export class ItemsEntryComponent implements OnChanges {

  // #region Forwarded to the check-off

  @Input() rows: CheckableLineItem[] = [];
  @Input() damageByLine: Record<string, LineDamage> = {};
  @Input() isLegacyPo = false;
  @Input() expandedIndices: number[] | null = null;
  @Input() isLineReadOnly: (row: CheckableLineItem) => boolean = () => false;
  @Input() readOnlyNote: (row: CheckableLineItem) => string = () => '';
  @Input() allowClearStatus = false;
  @Input() allowOffPoQtyEdit = false;
  @Input() allowOffPoRemove = false;
  @Input() linePanel: TemplateRef<unknown> | null = null;
  @Input() errorAtIndex: number | null = null;
  @Input() errorText = '';

  @Output() rowsChange = new EventEmitter<CheckableLineItem[]>();
  @Output() statusCleared = new EventEmitter<CheckableLineItem>();
  @Output() rowRemoved = new EventEmitter<CheckableLineItem>();
  @Output() expandedChange = new EventEmitter<number[]>();

  // #endregion

  // #region Adding an off-PO item

  /**
   * Whether to offer "Custom Add In".
   *
   * The edit screen turns it off on a PO-LESS log: the API stamps the log's own PO
   * onto every added receipt row, so a row added to a log with no PO is refused and
   * there is no way to fix it afterwards (§4.3/§3.5). Hiding the affordance beats
   * offering it and then rejecting the save.
   */
  @Input() allowAddOffPo = false;

  public showAddForm = false;
  public newItemDescription = '';
  public newItemQty = '';

  /**
   * The lowest synthetic `line` this control has ever SEEN or ISSUED — a watermark
   * that only ever goes down.
   *
   * The mint cannot read the current rows alone. A removed row leaves the array
   * immediately, so its number becomes reissuable the moment it is gone, and on the
   * edit screen that number is still the key of a STORED receipt row (see
   * `addItem`). Remembering the low-water mark is what makes a removed row's number
   * unreachable for the rest of this control's life, which is the only span in which
   * a stale pairing can be built.
   *
   * Lowered from every `rows` input as well as from every mint, because the numbers
   * the edit screen SEEDS were issued by `seedOffPoRows`, not here.
   */
  private _lowestLineSeen = 0;

  /** Keeps {@link _lowestLineSeen} at or below every line the host has handed over. */
  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes['rows']) { return; }
    this._lowestLineSeen = lowestLineIn(this.rows, this._lowestLineSeen);
  }

  public toggleAddForm(): void {
    this.showAddForm = !this.showAddForm;
    if (!this.showAddForm) {
      this.newItemDescription = '';
      this.newItemQty = '';
    }
  }

  /**
   * Strips the quantity field to digits.
   *
   * The input is `type="text"` with `inputmode="numeric"`, not `type="number"`:
   * Angular's number value accessor would turn the bound model into a number or
   * null, and this keeps it a string so a blank can be told from a zero. The strip
   * is what stops text being typed on desktop, where `inputmode` is a no-op.
   */
  public onQtyInput(): void {
    const digitsOnly = String(this.newItemQty ?? '').replace(/[^0-9]/g, '');
    if (digitsOnly !== this.newItemQty) { this.newItemQty = digitsOnly; }
  }

  /** A description is required; the quantity is not — some additions have no countable amount. */
  public get canAdd(): boolean {
    return this.newItemDescription.trim().length > 0;
  }

  /**
   * Appends an off-PO row.
   *
   * The synthetic `line` is NEGATIVE and counts down past every line already
   * present, which is what keeps `lineKey` unique across the whole set — positive
   * numbers belong to PO line sequences. The edit path relies on that same
   * numbering to pair a row back to its stored receipt, so it is a contract rather
   * than a display detail.
   *
   * ⚠️ Minted below the lowest line EVER SEEN, not below the lowest line currently
   * in use, and never from a COUNT.
   *
   * On the edit screen these numbers are how `planReceiptChanges` pairs a row back
   * to its STORED receipt row, so reissuing one silently reconciles the newly-typed
   * item against another item's ledger row — one description sticks, one row goes
   * missing, and the note reports the wrong item. Two ways to reissue, and both have
   * happened:
   *
   *   - counting the off-PO rows. Remove the first of two and the count drops to 1,
   *     so the next add mints −2, the number the SURVIVING row still holds.
   *   - minting below the lowest row PRESENT. A removed row is out of the array
   *     already, so its number is free again while its stored receipt is not:
   *     remove the LAST of two (or the only one) and the next add lands back on the
   *     removed row's number. `planReceiptChanges` then sees that group mentioned,
   *     keeps the row it was told to delete, and patches the new quantity onto it —
   *     with the old description, because a fresh row is not ragged and its
   *     description is never diffed.
   *
   * {@link _lowestLineSeen} closes both: it remembers seeded and issued numbers
   * alike and only ever decreases, so no number is ever handed out twice.
   *
   * `status: 'complete'` because an off-PO item is fully received by definition of
   * having been added: there is no ordered amount for it to be partial against.
   */
  public addItem(): void {
    if (!this.canAdd) { return; }

    const description = this.newItemDescription.trim();
    // Coerced defensively: even with a text input, a stray binding could hand over a
    // number or null, and String() keeps .trim() from throwing and silently eating Add.
    const qty = String(this.newItemQty ?? '').trim();
    // Over EVERY row, not just the off-PO ones: PO lines are positive, so they
    // cannot lower this, and reading the whole set means a future row numbered some
    // other way still cannot be collided with. Then floored by the watermark, so a
    // row that has been REMOVED still holds its number against the next add.
    const lowestLine = lowestLineIn(this.rows, this._lowestLineSeen);
    this._lowestLineSeen = lowestLine - 1;

    const row: CheckableLineItem = {
      line: lowestLine - 1,
      description,
      quantity: qty,
      unit: '',
      status: 'complete',
      receivedQty: '',
      totalQty: '',
      poLineSequence: null,
      isOffPo: true,
    };

    const next = [...this.rows, row];
    this.rows = next;
    this.rowsChange.emit(next);

    this.newItemDescription = '';
    this.newItemQty = '';
    this.showAddForm = false;
  }

  // #endregion
}

/**
 * The lowest `line` across a set of rows, never above `floor` and never above 0.
 *
 * 0 is the ceiling because off-PO lines are NEGATIVE by convention and PO lines are
 * positive: a set of PO lines alone must still leave the next mint at −1.
 */
function lowestLineIn(rows: readonly CheckableLineItem[] | null | undefined, floor: number): number {
  let lowest = Math.min(floor, 0);
  for (const row of rows ?? []) { lowest = Math.min(lowest, row.line ?? 0); }
  return lowest;
}
